import { nanoid } from 'nanoid';

import { GameOpponentsData } from '../../api/Rest/record';
import { ColorfulGameBG } from '../../constants/Games/gameBackground';
import { getGameTypeDetails } from '../../constants/Games/gameType';
import { Icons, IconType } from '../../constants/icons';
import { DefaultColors } from '../../constants/Theme/DefaultColors';
import { Uuid } from '../../models/Base';
import {
  ExtractDisplayDataFn,
  FetchDataFn,
  FileUrls,
  FinalizeRecordFn,
  GameControllerResult,
  GameInfo,
  InitializeRecordFn,
  OpponentsRecords,
  RefineResultFn,
} from '../../models/GameControllerStore';
import { GAME_THEME_COLOR } from '../../models/GameControllerStore/theme';
import { GameReply, GameType } from '../../models/GameStore';
import {
  GameRecord,
  GameRecordJson,
  GameRecordType,
} from '../../models/RecordStore';
import { ProfileJson } from '../../models/UserStore';
import { useFileStore } from '../../stores/FileStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSpaceStore } from '../../stores/SpaceStore';
import { getFromI18n } from '../../utils/i18n';
import { areFloatsEqual } from '../../utils/math';
import { computeReward, isFirstTimeGamePlay } from '../../utils/record';
import { getContentLocale, getSpokenLocale } from '../../utils/space';

import { fetchGameOpponents, fetchGameRecords, fetchGames } from './fetch-data';
import { GameControllerDefaultParams } from './mode-default';
import {
  GameIntermediate,
  prepareGameIntermediate,
} from './prepare-intermediate';
import {
  applyLocaleAndVariant,
  combinePairingGames,
  generateDummies,
  resolveAutoTimeLimit,
} from './refine-result';

export type GameControllerRecordParams = GameControllerDefaultParams & {
  gameRecordId: Uuid;
};

type RecordJson = { gameRecord: GameRecordJson };
type ResultRecords = {
  gameRecords: GameRecord[];
  // revisionSchedule: RevisionSchedule | null;
  newRecord: RecordJson | undefined;
};

export type GameControllerRecordResult = GameControllerResult<ResultRecords> & {
  dummies: OpponentsRecords;
};

const getBodyColor = (theme: number | undefined): string => {
  return (
    GAME_THEME_COLOR[(theme ?? 0) % GAME_THEME_COLOR.length] ??
    GAME_THEME_COLOR[0]
  ).backgroundColor;
};
const getBodyArrowColor = (theme: number | undefined): string => {
  return (
    GAME_THEME_COLOR[(theme ?? 0) % GAME_THEME_COLOR.length] ??
    GAME_THEME_COLOR[0]
  ).arrow;
};
// should be the similar to mode-default
const extractDisplayData: ExtractDisplayDataFn<
  GameControllerRecordParams,
  GameIntermediate,
  GameControllerRecordResult
> = (
  { locale, userId },
  { classroomId, gameId, gameSetId },
  intermediate,
  result,
) => {
  // const { getGameCollection } = useGameStore.getState();
  const { getAlbum, getSpace, getMembership } = useSpaceStore.getState();
  const { getGameRecords } = useRecordStore.getState();
  const { getFileDownload } = useFileStore.getState();

  const space = getSpace(classroomId);
  const membership = getMembership(classroomId, userId);
  const album = getAlbum(gameSetId);
  // const partSummary = intermediate
  //   ? getAlbumSummary(gameSetId)?.parts[intermediate.partIndex]
  //   : undefined;
  const game = result?.gameSequence?.[result.defaultIndex];
  const latestGameRecord = result?.records?.gameRecords?.[0];
  const showRecords =
    getGameRecords('gamePlay')?.filter((record) => record.isSlideshow) ?? [];
  const partTheme = intermediate?.partTheme;
  const isFirstTimePlay = isFirstTimeGamePlay(
    getGameRecords,
    gameSetId,
    gameId,
  );
  const variant = game?.gameData;
  const customIcon = intermediate?.icon?.tag
    ? getGameTypeDetails(intermediate?.icon?.tag).icon
    : undefined;
  const spaceIcon = space?.icon
    ? getFileDownload(space.icon)?.getVersion()?.uri
    : undefined;
  const { reward: maximumReward, memberReward } = computeReward(
    membership?.isAtLeastMember,
    result && intermediate?.gameReward ? intermediate?.gameReward : {},
    false,
    5,
    result?.records?.gameRecords.map((record) => record.reward) ?? [],
    1,
    intermediate?.gameAnswerSlots,
  );
  const { reward: maximumShowReward } = computeReward(
    membership?.isAtLeastMember,
    result && intermediate?.slideshowReward
      ? intermediate?.slideshowReward
      : {},
    false,
    5,
    showRecords.map((record) => record.reward),
    1,
    0,
  );

  // const gameBackgrounds =
  //   album?.content?.gameDesign.backgrounds.education.concat(
  //     album.content.gameDesign.backgrounds.enterprise,
  //   );

  // const gameBGIndex = gameBackgrounds
  //   ? gameBackgrounds[Math.floor(gameBackgrounds.length * Math.random())]?.set
  //   : undefined;

  const gameBGIndex = Math.floor(ColorfulGameBG.length * Math.random());

  return {
    classroomId,
    gameSetId,
    theme: intermediate?.gameType,
    isChallenge: false,
    oneTimeFee: true,
    gameBGIndex: gameBGIndex,
    context: {
      icon: spaceIcon ? spaceIcon : Icons[IconType.Classroom],
      title: getFromI18n(space?.i18nClassroomInfo, locale)?.name ?? '',
      color: DefaultColors.green.OPPONENT_GREEN,
    },
    progress: {
      icon: Icons[IconType.Game],
      title: getFromI18n(intermediate?.i18nPartInfo, locale)?.name ?? '',
      completed: 0, // partSummary?.completedGameCount ?? 0,
      total:
        (intermediate?.partIndex !== undefined
          ? album?.content?.partStatistics?.[intermediate.partIndex]?.gameCount
          : 0) ?? 0,
    },
    body: {
      index: intermediate?.gameItems.findIndex(
        (item) => item.game.gameId === gameId,
      ),
      icon: customIcon,
      title: game?.gameData?.title ?? '',
      color: getBodyColor(partTheme),
      arrowColor: getBodyArrowColor(partTheme),
      gameIconTag: intermediate?.icon?.tag,
    },
    latestRecord: latestGameRecord
      ? {
          score: latestGameRecord.score,
          durationInMs: latestGameRecord.durationInMs,
        }
      : undefined,
    completedBefore:
      result?.records?.gameRecords?.some((r) => r.completed) ?? false,
    maximumReward,
    maximumShowReward,
    fee: !isFirstTimePlay ? {} : intermediate?.unlockFee ?? {},
    statistics: game?.gameStatistics
      ? {
          playCount: game.gameStatistics.playCount ?? 0,
          averageScore: game.gameStatistics.averageScore,
        }
      : undefined,
    partIndex: intermediate?.partIndex,
    memberReward: memberReward ?? undefined,
    customReplies:
      variant !== undefined && 'replies' in variant
        ? (variant.replies as GameReply[])
        : undefined,
    vsMode: !!result?.dummies,
    dummies: result?.dummies,
  };
};

type GameControllerRecordData = {
  games: GameInfo[];
  gameRecords: GameRecord[];
  // revisionSchedule: RevisionSchedule | null;
  opponents: GameOpponentsData & { profiles: { profile: ProfileJson }[] };
  newRecord: RecordJson | undefined;
};

const fetchData: FetchDataFn<
  GameControllerRecordParams,
  GameControllerRecordData,
  GameIntermediate
> = async ({ userId }, params, { gameItems }) => {
  const games = await fetchGames(params, gameItems);
  const gameProgress =
    gameItems.length > 0
      ? games.filter((game) => game !== undefined).length / gameItems.length
      : 0;

  const gameRecords = await fetchGameRecords(
    { ...params, userId },
    (r) => r.isNormal,
    true,
  );
  const gameRecordProgress = gameRecords === undefined ? 0 : 1;

  // const revisionSchedule = await fetchRevisionSchedule(
  //   { ...params, userId },
  //   true,
  // );
  // const revisionScheduleProgress = revisionSchedule === undefined ? 0 : 1;

  const progress = gameProgress * 0.6 + gameRecordProgress * 0.4;
  // + revisionScheduleProgress * 0.2;
  if (
    areFloatsEqual(progress, 1) &&
    gameRecords !== undefined
    // && revisionSchedule !== undefined
  ) {
    const opponents = await fetchGameOpponents(params);
    const newRecord: GameRecordJson | undefined = gameRecords
      .find((r) => r.gameRecordId === params.gameRecordId)
      ?.toJSON();
    const otherRecords = gameRecords.filter(
      (r) => r.gameRecordId !== params.gameRecordId,
    );
    // await fetchPuzzles(params, userId, 1, otherRecords);

    return {
      result: {
        games: games as GameInfo[],
        gameRecords: otherRecords,
        // revisionSchedule,
        opponents,
        newRecord: newRecord ? { gameRecord: newRecord } : undefined,
      },
      progress: 1,
    };
  }
  return { progress };
};

const refineResult: RefineResultFn<
  GameControllerRecordParams,
  GameControllerRecordData,
  GameIntermediate,
  GameControllerRecordResult
> = (
  { userId },
  { classroomId, gameId },
  { gameItems },
  {
    games,
    gameRecords,
    // revisionSchedule,
    opponents,
    newRecord,
  },
) => {
  const { getSpace, getMembership } = useSpaceStore.getState();

  const classroom = getSpace(classroomId);
  const membership = getMembership(classroomId, userId);
  const contentLocale = getContentLocale(classroom, membership);
  const soundLocale = getSpokenLocale(classroom, membership);

  const game = resolveAutoTimeLimit(
    combinePairingGames(
      games.map((g) =>
        applyLocaleAndVariant(
          g,
          contentLocale,
          soundLocale,
          gameRecords?.length,
        ),
      ),
      true,
    ).find((g) => g.gameId === gameId),
  );

  if (game && !Object.values(GameType).includes(game.gameType)) {
    throw new Error('Invalid game type - ' + game.gameType);
  }

  return {
    gameSequence: gameItems.map((item) =>
      item.game.gameId === gameId ? game : undefined,
    ),
    defaultIndex: gameItems.findIndex((item) => item.game.gameId === gameId),
    fileUrls: games.reduce(
      (acc, cur) => {
        cur.dependencies.images.forEach((id) => {
          acc.images[id] = undefined;
        });
        cur.dependencies.audios.forEach((id) => {
          acc.audios[id] = undefined;
        });
        cur.dependencies.videos.forEach((id) => {
          acc.videos[id] = undefined;
        });
        cur.dependencies.resourceImages.forEach((id) => {
          acc.resourceImages[id] = undefined;
        });
        cur.dependencies.strokes.forEach((id) => {
          acc.strokes[id] = undefined;
        });
        cur.dependencies.ttsAudios.forEach((id) => {
          acc.ttsAudios[id] = undefined;
        });
        return acc;
      },
      {
        images: {},
        audios: {},
        videos: {},
        resourceImages: {},
        strokes: {},
        ttsAudios: {},
      } as FileUrls,
    ),
    records: {
      gameRecords,
      // revisionSchedule,
      newRecord,
    },
    dummies: generateDummies(
      { gameId, opponents },
      newRecord?.gameRecord?.result?.opponentRecord,
    ),
  };
};

export const initializeRecord: InitializeRecordFn<
  GameControllerRecordParams,
  GameIntermediate,
  GameControllerRecordResult,
  RecordJson
> = async (
  { userId },
  { classroomId, gameSetId, gameId },
  { gameCollectionId },
  { gameSequence, defaultIndex },
  { fee },
) => {
  const now = Date.now();
  const game = gameSequence[defaultIndex];
  if (game === undefined) {
    throw new Error('Invalid game');
  }
  const record: GameRecordJson = {
    gameRecordId: nanoid(),
    createdAt: now,
    classroomId,
    gameSetId,
    gameCollectionId,
    gameId,
    gameHash: game.hash,
    type: GameRecordType.Normal,
    playedBy: userId,
    sessionStartedAt: now,
    fee,
    startedAt: now,
  };
  await Promise.resolve();
  // await useRecordStore.getState().syncGameRecord(record, false);
  return { gameRecord: record };
};

export const finalizeRecord: FinalizeRecordFn<
  GameControllerRecordParams,
  GameIntermediate,
  GameControllerRecordResult,
  RecordJson
  // eslint-disable-next-line @typescript-eslint/require-await
> = async (_utils, _params, _intermediate, { records }, { record }) => {
  const { clearPlayingRecord } = useRecordStore.getState();

  clearPlayingRecord(); // FIXME: no more playing records because await when init

  const newRecord = records.newRecord ?? record;

  const isBestInType =
    (newRecord.gameRecord.score ?? 0) > 0 &&
    records.gameRecords.every((r) => !r.isBetterThan(newRecord.gameRecord));

  const increaseMissionProgress = false;
  // TODO: increase mission progress
  // if (records.gameRecords.length === 0) {
  //   increaseMissionProgress = true;
  //   const missionId = ['daily-game-custom-1', 'daily-game-custom-2'].reduce(
  //     (acc, id) => {
  //       if (!acc && getDailyMissionCellData(rootStore, id)) {
  //         return id;
  //       }
  //       return acc;
  //     },
  //     null,
  //   );
  //   if (missionId) {
  //     const missionProgress = store.user.getMission(missionId);
  //     const quantity = (missionProgress.progress?.task?.quantity || 0) + 1;
  //     if (quantity === missionProgress.mission.task.quantity) {
  //       addNewCollectRewardMissionId(missionId);
  //     }
  //     store.user.updateMissionProgress({ missionId, quantity });
  //   }
  // }

  return {
    record: newRecord,
    isBestInType,
    increaseMissionProgress,
  };
};

export const preprocess = {
  extractDisplayData,
  prepareIntermediate: prepareGameIntermediate,
  fetchData,
  refineResult,
  initializeRecord,
  finalizeRecord,
};
